import React from 'react';

import { fetchCommissions, fetchPerformance, getAffiliateError } from '../api';
import {
  monthKey,
  previousMonthKey,
  previousRange,
  topEntities,
  type ChartMode,
  type DateRange,
  type Dimension,
  type PeriodPreset,
} from '../reports-helpers';
import type { AffiliateCommissionsReport, AffiliateMetrics, AffiliatePerformanceRow } from '../types';

/** One entity's daily performance rows, for a line in the by-dimension trend. */
export interface DimensionSeries {
  id: string;
  name: string;
  rows: AffiliatePerformanceRow[];
}

export interface AffiliateReportsData {
  loading: boolean;
  error?: string;
  errorCode?: string;
  currency: string;
  /** Range-scoped performance totals (current) and the previous comparison range (for deltas). */
  totals?: AffiliateMetrics;
  prevTotals?: AffiliateMetrics;
  /** Daily `group_by=date` rows for the current range (chart compare-metrics mode + KPI sparklines). */
  dailyRows: AffiliatePerformanceRow[];
  /** Breakdown rows for the active dimension (affiliate/campaign), or the daily rows when dimension = date. */
  breakdownRows: AffiliatePerformanceRow[];
  /** Month-scoped commissions (month of range.to) and the previous month (for deltas). */
  commissions?: AffiliateCommissionsReport;
  prevCommissions?: AffiliateCommissionsReport;
  /** Per-entity daily series for the by-dimension multi-line trend (empty unless that mode is active). */
  dimensionSeries: DimensionSeries[];
  reload: () => void;
}

/** How many lines the by-dimension multi-line trend draws (top-N by NGR). */
export const MAX_TREND_SERIES = 5;

const EMPTY: Omit<AffiliateReportsData, 'reload'> = {
  loading: true,
  currency: 'USD',
  dailyRows: [],
  breakdownRows: [],
  dimensionSeries: [],
};

/**
 * The Reports dashboard data layer, keyed on the current selection. Independent of
 * `useAffiliatesRoster` (which stays as the roster/overview backbone). All calls are
 * best-effort except the primary daily-performance fetch, whose hard failure is the
 * dashboard's error (e.g. AFFILIATES_EMAIL_NOT_VERIFIED); a flaky secondary call only
 * degrades its own widget. A monotonic request id guards against out-of-order landings
 * when the selection changes faster than the network (latest write wins).
 */
export function useAffiliateReports(
  casinoInstanceId: string | undefined,
  range: DateRange,
  preset: PeriodPreset,
  dimension: Dimension,
  chartMode: ChartMode,
): AffiliateReportsData {
  const [reloadTick, setReloadTick] = React.useState(0);
  const [state, setState] = React.useState<Omit<AffiliateReportsData, 'reload'>>(EMPTY);
  const reqIdRef = React.useRef(0);

  const { from, to } = range;
  // Multi-line series are only needed in by-dimension mode over affiliate/campaign
  // (a "date" dimension IS the time axis, so it stays single-series).
  const wantsSeries = chartMode === 'dimension' && dimension !== 'date';

  React.useEffect(() => {
    if (!casinoInstanceId) {
      setState(EMPTY);
      return;
    }
    const reqId = ++reqIdRef.current;
    const isStale = (): boolean => reqId !== reqIdRef.current;
    setState((prev) => ({ ...prev, loading: true, error: undefined, errorCode: undefined }));

    const today = new Date();
    const prev = previousRange(range, preset, today);
    const month = monthKey(to);

    (async () => {
      // Primary (backbone): a hard failure here is the dashboard error.
      const daily = await fetchPerformance(casinoInstanceId, { group_by: 'date', from, to });
      const [prevTotals, breakdown, commissions, prevCommissions] = await Promise.all([
        fetchPerformance(casinoInstanceId, { group_by: 'total', from: prev.from, to: prev.to }).catch(() => undefined),
        dimension === 'date'
          ? Promise.resolve(daily)
          : fetchPerformance(casinoInstanceId, { group_by: dimension, from, to }).catch(() => undefined),
        fetchCommissions(casinoInstanceId, month).catch(() => undefined),
        fetchCommissions(casinoInstanceId, previousMonthKey(month)).catch(() => undefined),
      ]);

      let dimensionSeries: DimensionSeries[] = [];
      if (wantsSeries && breakdown?.rows?.length) {
        const tops = topEntities(breakdown.rows, dimension, 'ngr_usd', MAX_TREND_SERIES, '');
        dimensionSeries = await Promise.all(
          tops.map(async (entity) => {
            const filter = dimension === 'affiliate' ? { affiliate_id: entity.id } : { campaign: entity.id };
            const perf = await fetchPerformance(casinoInstanceId, { group_by: 'date', from, to, ...filter }).catch(
              () => undefined,
            );
            return { id: entity.id, name: entity.name, rows: perf?.rows ?? [] };
          }),
        );
      }

      return {
        currency: daily.currency ?? commissions?.currency ?? 'USD',
        totals: daily.totals,
        prevTotals: prevTotals?.totals,
        dailyRows: daily.rows ?? [],
        breakdownRows: (dimension === 'date' ? daily.rows : breakdown?.rows) ?? [],
        commissions,
        prevCommissions,
        dimensionSeries,
      };
    })()
      .then((next) => {
        if (isStale()) return;
        setState({ ...next, loading: false });
      })
      .catch((err: unknown) => {
        if (isStale()) return;
        const { message, code } = getAffiliateError(err);
        setState({ ...EMPTY, loading: false, error: message, errorCode: code });
      });
    // `range` is spread into from/to below so the object identity does not thrash the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casinoInstanceId, from, to, dimension, chartMode, wantsSeries, preset, reloadTick]);

  const reload = React.useCallback(() => setReloadTick((tick) => tick + 1), []);

  return { ...state, reload };
}
